import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Sparkles, ArrowRight } from "lucide-react";

const STORAGE_KEY = "kore_exit_intent_shown";
const HIDE_PATHS = ["/admin", "/scorecard", "/booking"];

export default function ExitIntentModal() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const hidden = HIDE_PATHS.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (hidden) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let mobileTimer = null;
    let armed = false;
    const arm = () => { armed = true; };
    // Only arm after 15 seconds so we don't ambush new visitors instantly
    const armTimer = setTimeout(arm, 15000);

    const trigger = () => {
      if (!armed || sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.setItem(STORAGE_KEY, "1");
      setOpen(true);
    };

    // Desktop: mouse leaves through the top of the viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && e.relatedTarget == null) trigger();
    };
    document.addEventListener("mouseout", onMouseOut);

    // Mobile: fast scroll up after being on the page for >20s
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (armed && y < lastY - 80 && y < 400) trigger();
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Fallback: mobile users who idle for 45s
    mobileTimer = setTimeout(() => {
      if (window.matchMedia("(pointer: coarse)").matches) trigger();
    }, 45000);

    return () => {
      clearTimeout(armTimer);
      clearTimeout(mobileTimer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, [hidden, pathname]);

  if (!open) return null;

  const dismiss = () => setOpen(false);

  return (
    <div
      data-testid="exit-intent-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-kore-forest/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={dismiss}
    >
      <div
        className="relative bg-kore-warm rounded-3xl max-w-lg w-full overflow-hidden shadow-lift border border-kore-gold/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          data-testid="exit-intent-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-kore-ink/50 hover:text-kore-forest transition p-1 rounded-full z-10"
        >
          <X size={18} />
        </button>

        <div className="bg-kore-forest text-kore-warm px-7 pt-8 pb-6 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-kore-light">
            <Sparkles size={13} /> Before you go
          </div>
          <h2 className="font-display font-light text-3xl md:text-4xl mt-4 leading-tight">
            Not sure where to start?<br/>
            <span className="italic text-kore-light">Take the 3-minute Scorecard.</span>
          </h2>
        </div>

        <div className="px-7 py-7">
          <p className="text-sm text-kore-ink/75 leading-relaxed">
            18 honest questions across strength, mobility, recovery, nutrition, mindset and community. In three minutes you'll know exactly where you're strong, where to focus, and what to change first — with a personalised report emailed straight to you.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/scorecard"
              onClick={dismiss}
              data-testid="exit-intent-scorecard"
              className="btn-primary flex-1 justify-center"
            >
              Take the scorecard <ArrowRight size={14} />
            </Link>
            <Link
              to="/free-guide"
              onClick={dismiss}
              data-testid="exit-intent-guide"
              className="btn-ghost flex-1 justify-center"
            >
              Or grab the free guide
            </Link>
          </div>
          <button
            onClick={dismiss}
            data-testid="exit-intent-dismiss"
            className="mt-5 text-[11px] uppercase tracking-[0.22em] text-kore-ink/50 hover:text-kore-forest w-full text-center"
          >
            No thanks — keep browsing
          </button>
        </div>
      </div>
    </div>
  );
}
